import { Component, signal, computed, Input, effect, EnvironmentInjector, inject, createComponent } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LookupProfile } from '../../services/LookupService';
import { GenericListComponent } from '../list-components/generic-base-list/generic-list.component';
import { INeedRefreshData } from '../_primitives/INeedRefreshData';



@Component({
  selector: 'feature-mediator',
  template: `<ng-content></ng-content>`,
  standalone: true,
  exportAs: 'mediator'
})
export class FeatureMediatorComponent<RootType extends { id: any } = { id: any }, Level1Type extends { id: number } = { id: number }> {
  @Input() shouldFetchLookupsOnRootSelect: boolean = true;

  selectedRoot = signal<RootType | null>(null);
  selectedLevel1 = signal<Level1Type | null>(null);

  needRefreshData = signal<INeedRefreshData | null>(null);

  selectedRootId = computed(() => this.selectedRoot()?.id ?? null);
  selectedLevel1Id = computed(() => this.selectedLevel1()?.id ?? null);


  @Input() rootDataProvider: ((root: RootType) => any[]) | null = null; // Fournisseur de donnees pour level1 à partir de root
  @Input() level1DataProviders: Record<string, (level1: Level1Type) => any[]> = {}; // Fournisseurs pour sub-levels (ex: truckerActivities, truckers, docks)
  @Input() nestedListTypes: (typeof GenericListComponent<any>)[] = [];

  private injector = inject(EnvironmentInjector);
  private entityNameCache = new Map<any, string>();


  // Cache pour les computed signals des sub-levels
  private subLevelDataCache = new Map<string, any>();

  // Computed signals pour les données nested (génériques et configurables)
  level1Data = computed(() => {
    const root = this.selectedRoot();
    return root && this.rootDataProvider ? this.rootDataProvider(root) : [];
  });

  constructor() {
    effect(() => {
      let needRefresh = this.needRefreshData();
      if (needRefresh) {
        // TODO: Trigger refresh
      }
    });
  }

  getSubLevelData(key: string) {
    // Retourner le computed existant du cache ou en créer un nouveau
    if (!this.subLevelDataCache.has(key)) {
      this.subLevelDataCache.set(key, computed(() => {
        const level1 = this.selectedLevel1();
        const provider = this.level1DataProviders[key];
        if (level1 && provider) {
          return provider(level1);
        }
        // Si pas de level1 sélectionné, agréger depuis tous les level1
        const datas = this.level1Data().flatMap((item: Level1Type) => provider ? provider(item) : []).filter(
          (item, index, self) =>
            index === self.findIndex(t => t.id === item.id)
        );//TODO : faire un distinct ou filter pour eviter les doublons
        return datas;
      }));
    }
    return this.subLevelDataCache.get(key)!;
  }

  // Computed pour les otherLookupProfiles (collectes des nestedListTypes)
  otherLookupProfiles = computed(() => {
    const allProfiles = this.nestedListTypes.flatMap(type => type.lookupProfiles);
    return this.uniqueLookupProfiles(allProfiles);
  });

  // Computed signal qui retourne la liste des noms d'entités
  public entityNames = computed(() => {
    return this.nestedListTypes
      .map(type => this.getEntityName(type))
      .filter(name => name.length > 0);
  });

  // Cles d'etat des listes imbriquees. L'etat des filtres est indexe par Namespace.Entite,
  // information que entityNames() ne porte pas.
  public nestedListKeys = computed(() => {
    return this.nestedListTypes
      .map(type => this.getListKey(type))
      .filter(key => key.length > 0);
  });


  selectRoot(item: RootType | null) {
    this.selectedRoot.set(item);
    this.selectedLevel1.set(null); // Reset level1 quand root change
    // Logique supplémentaire : notifier ou updater services si besoin
  }

  selectLevel1(item: Level1Type | null) {
    this.selectedLevel1.set(item);
  }

  // Getters utilitaires
  get fkLevel1Id(): number | null {
    return this.selectedLevel1()?.id ?? null;
  }

  get hasRootSelected(): boolean {
    return this.selectedRoot() !== null;
  }

  bindSelectionEvent(componentType: 'root' | 'level1', eventEmitter: any) {
    // Exemple : binder un (itemSelected) à la sélection correspondante
    eventEmitter.subscribe((item: any) => {
      if (componentType === 'root') {
        this.selectRoot(item);
      } else if (componentType === 'level1') {
        this.selectLevel1(item);
      }
    });
  }

  // Helper pour unifier les LookupProfiles par lookupName
  private uniqueLookupProfiles(profiles: LookupProfile[]): LookupProfile[] {
    const seen = new Set<string>();
    return profiles.filter(profile => {
      let uniqueKey: string;

      if (profile.lookupNamespace) {
        // Avec namespace: combinaison de lookupName et lookupNamespace
        uniqueKey = `${profile.lookupName}-${profile.lookupNamespace}`;
      } else {
        // Sans namespace: seulement lookupName (avec préfixe spécial)
        uniqueKey = `${profile.lookupName}`;
      }
      if (seen.has(uniqueKey)) {
        return false;
      }
      seen.add(profile.lookupName);
      return true;
    });
  }

  private listKeyCache = new Map<any, string>();

  private getListKey(componentType: any): string {
    if (this.listKeyCache.has(componentType)) {
      return this.listKeyCache.get(componentType)!;
    }

    try {
      const componentRef = createComponent(componentType, {
        environmentInjector: this.injector
      });
      const listKey = (componentRef.instance as any).listKey ?? '';
      componentRef.destroy();
      this.listKeyCache.set(componentType, listKey);
      return listKey;
    } catch (err) {
      console.error(`Impossible de lire la cle de liste de ${componentType.name}`, err);
      return '';
    }
  }

  private getEntityName(componentType: any): string {
    if (this.entityNameCache.has(componentType)) {
      return this.entityNameCache.get(componentType)!;
    }

    try {
      // Créer une instance du composant (sans attachement DOM)
      const componentRef = createComponent(componentType, {
        environmentInjector: this.injector
      });
      const instance = componentRef.instance as any;
      const entityName = instance.entityMetaData.EntityName;
      this.entityNameCache.set(componentType, entityName);
      // Détruire immédiatement l'instance pour éviter les fuites
      componentRef.destroy();
      return entityName;
    } catch (err) {
      console.error(`Impossible d'instancier ${componentType.name}`, err);
      return '';
    }
  }
}
